import { withRequestParsing } from '@lib/mixins/with-request-parsing'
import { type BasePageProps, withServices } from '@lib/mixins/with-services'
import { withTelemetry } from '@lib/mixins/with-telemetry'
import { navHelper } from '@lib/services/navigation'

import type { TypePageEntry } from '@contentful-types/page'
import type { TypeSeoMetadataEntry } from '@contentful-types/seo-metadata'
import type { TypeSocialMetadataEntry } from '@contentful-types/social-metadata'

import { IS_BROWSER } from '@constants/globals'

import { CmsPage } from '@components/page-core/page'
import { getHomepageHeroSeed } from '@components/page-head/utils'

import CustomPage from '@custom/base/custom-page'
import { isCustomPage } from '@custom/registry/utils'

import Custom500 from './500'

interface RenderProps extends BasePageProps {
  page?: TypePageEntry
  pageSlug?: string
  heroImageSeed?: number | null
  metadata: {
    social?: TypeSocialMetadataEntry
    seo?: TypeSeoMetadataEntry
    enabledFlags?: string[]
  }
}

const Render = withServices(
  withTelemetry(({ page, pageSlug, heroImageSeed, metadata }: RenderProps) => {
    if (navHelper.currentQueryParams.debugClientError === 'true' && IS_BROWSER) {
      throw new Error('Test error')
    }

    // Check if this page should use custom rendering
    if (pageSlug && isCustomPage(pageSlug)) {
      return (
        <CustomPage
          slug={pageSlug}
          metadata={metadata}
          heroImageSeed={heroImageSeed}
          enabledFlags={metadata.enabledFlags}
        />
      )
    }

    // Handle regular CMS page rendering
    if (!page || pageSlug === '/500') {
      return <Custom500 />
    }
    return <CmsPage data={page} metadata={metadata} />
  }),
)

export const getServerSideProps = withRequestParsing<RenderProps>(
  async ({ requestData, context }) => {
    if (context.query.debugRenderError === 'true') {
      throw new Error('Test error')
    }

    return {
      page: requestData?.pageEntry,
      pageSlug: requestData?.pageSlug,
      heroImageSeed: getHomepageHeroSeed(requestData?.pageSlug),
      metadata: {
        ...(requestData?.metadata?.social && { social: requestData.metadata.social }),
        ...(requestData?.metadata?.seo && { seo: requestData.metadata.seo }),
        ...(requestData?.metadata?.enabledFlags && {
          enabledFlags: requestData.metadata.enabledFlags,
        }),
      },
    }
  },
)

export default Render
